import {useCallback, useState, type ReactNode} from 'react'
import {createPortal} from 'react-dom'
import {WebGLDither} from '@github-ui/brand-webgl'
import styles from './DitherBackground.module.css'
import {useBrandTreatment} from '../useBrandTreatment'

// Debounce window for coalescing container resizes (e.g. while images load)
// before remounting the dither.
interface UseDitherBackgroundOptions {
  enabled?: boolean
}

type UseDitherBackgroundReturn = {
  containerRef: React.RefCallback<HTMLDivElement>
  containerEl: HTMLDivElement | null
  DitherBackground: () => ReactNode
}

/**
 * Portals a `WebGLDither` background into a container captured via callback ref.
 * Needed because Primer Brand's `Hero` filters out arbitrary children:
 * https://github.com/primer/brand/issues/1356
 *
 * `enabled` lives in the hook so callers can opt out without splitting their
 * render path. That keeps the effect's setup and cleanup logic in one place,
 * and lets React rerun or tear down the observer when the opt-in state changes.
 */
export function useDitherBackground({enabled = true}: UseDitherBackgroundOptions): UseDitherBackgroundReturn {
  const [containerEl, setContainerEl] = useState<HTMLDivElement | null>(null)
  const containerRef = useCallback((element: HTMLDivElement | null) => {
    setContainerEl(element)
    if (!element) return

    element.classList.add(styles.ditherHost)

    return () => {
      element.classList.remove(styles.ditherHost)
    }
  }, [])

  const {dither} = useBrandTreatment()

  const DitherBackground = useCallback(() => {
    if (!enabled || !containerEl) return null
    const className = styles.ditherBackground

    return createPortal(<WebGLDither className={className} {...dither} />, containerEl)
  }, [containerEl, dither, enabled])

  return {containerRef, containerEl, DitherBackground}
}
